import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {TrustedHTML, TrustedTypePolicy} from '@github-ui/trusted-types'
// eslint-disable-next-line no-restricted-imports -- This IS the sanitize facade; it wraps dompurify.
import DOMPurify, {type Config as DOMPurifyConfig} from 'dompurify'

import {addInlineSanitizeHooks, INLINE_SANITIZE_CONFIG} from './configs/inline'
import {addMarkdownSanitizeHooks, MARKDOWN_SANITIZE_CONFIG} from './configs/markdown'
import {addMathSanitizeHooks, MATH_SANITIZE_CONFIG} from './configs/math'
import {applyLaxSecurityHooks} from './configs/shared-hooks'
import type {SafeHTMLString, SanitizedHTML} from './safe-html-string'
import {
  getInlineSanitizePolicy,
  getMarkdownSanitizePolicy,
  getMathSanitizePolicy,
  getSanitizePolicy,
  mintInlineTrustedHTML,
  mintMarkdownTrustedHTML,
  mintMathTrustedHTML,
  mintTrustedHTML,
} from './trusted-types'
import type {PresetConfig, Purifier, SanitizeConfig} from './types'

// Attach the shared security hooks to the lax default singleton ONCE at module
// init so a caller-supplied `domPurifyConfig` cannot clear them (github/react-platform#1726). Each hook
// guards internally on the `sanitize_dompurify_hardening` flag, so this is a safe
// no-op while hardening is off (the lax path stays legacy-permissive until github/react-platform#1725
// flips the gate).
applyLaxSecurityHooks(DOMPurify as unknown as Purifier)

/**
 * Sanitize HTML using DOMPurify with the browser's native window.
 *
 * This is the browser entry point — on the server, the "node" condition
 * in package.json exports resolves to sanitize.server.ts instead.
 *
 * String results are routed through the private `github-sanitize-lax`
 * Trusted Types policy when the `trusted_types_policies` feature
 * flag is on and `window.trustedTypes` is available, so that the output
 * can be safely assigned to TrustedHTML sinks in CSP-`trusted-types`-
 * enforced contexts.
 *
 * @param html - The HTML string to sanitize
 * @param config - Optional DOMPurify Config object, or a named preset
 *   (`{preset: 'markdown | inline | math'}`). A preset fully defines behavior
 *   and cannot be combined with DOMPurify overrides.
 */
export function sanitize(dirty: string | Node, cfg: PresetConfig): SanitizedHTML
export function sanitize(dirty: string | Node, cfg: DOMPurifyConfig & {RETURN_DOM: true; preset?: never}): Node
export function sanitize(
  dirty: string | Node,
  cfg: DOMPurifyConfig & {RETURN_DOM_FRAGMENT: true; preset?: never},
): DocumentFragment
export function sanitize(
  dirty: string | Node,
  cfg: DOMPurifyConfig & {RETURN_TRUSTED_TYPE: true; preset?: never},
): TrustedHTML
export function sanitize(
  dirty: string | Node,
  cfg: DOMPurifyConfig & {RETURN_TRUSTED_TYPE: false; preset?: never},
): SafeHTMLString
export function sanitize(dirty: string | Node, cfg?: DOMPurifyConfig & {preset?: never}): SanitizedHTML
export function sanitize(dirty: string | Node, cfg?: SanitizeConfig): SanitizedHTML | Node | DocumentFragment {
  // A named preset fully owns its config, hooks, and Trusted Types policy —
  // route it to the dedicated preset path, ignoring the default purifier.
  if (isPresetConfig(cfg)) {
    switch (cfg.preset) {
      case 'inline':
        return sanitizeInline(dirty)
      case 'markdown':
        return sanitizeMarkdown(dirty)
      case 'math':
        return sanitizeMath(dirty)
    }
  }
  // sanitize() is sanitizeWithPurifier() bound to the module-level DOMPurify singleton.
  return sanitizeWithPurifier(dirty, DOMPurify as unknown as Purifier, cfg)
}

/**
 * Narrow a config union to its preset arm. A raw DOMPurify config never carries
 * a `preset` key (enforced by the `SanitizeConfig` type), so a present `preset`
 * unambiguously selects the preset path.
 */
function isPresetConfig(cfg: SanitizeConfig | undefined): cfg is PresetConfig {
  return cfg != null && 'preset' in cfg && cfg.preset != null
}

// The markdown preset's purifier is a DOMPurify instance carrying the markdown
// security hooks. Built lazily on first markdown use and memoized for the page
// lifetime, mirroring the module-level default singleton.
let markdownPurifier: Purifier | undefined
function getMarkdownPurifier(): Purifier {
  // selfHardens: true — the preset callback attaches the unconditional
  // applySecurityHooks itself, so skip the auto lax-hook attach to avoid
  // double-running the js-*/data-*/image hooks. See createInternalPurifier below.
  markdownPurifier ??= createInternalPurifier(addMarkdownSanitizeHooks, {selfHardens: true})
  return markdownPurifier
}

/**
 * Sanitize through the `markdown` preset: the hardened markdown config + hooks,
 * minted through the dedicated `github-sanitize-markdown` Trusted Types policy.
 * Unlike the default path this ignores the `sanitize_dompurify_hardening` flag —
 * the caller explicitly opted into hardening by selecting the preset.
 */
function sanitizeMarkdown(dirty: string | Node): SanitizedHTML {
  const policy = getMarkdownSanitizePolicy()
  // Inject the markdown policy so DOMPurify's internal minting stays on
  // `github-sanitize-markdown` rather than lazily creating its own `dompurify` policy.
  const effectiveCfg = policy
    ? {...MARKDOWN_SANITIZE_CONFIG, TRUSTED_TYPES_POLICY: policy as TrustedTypePolicy}
    : MARKDOWN_SANITIZE_CONFIG
  const result = getMarkdownPurifier().sanitize(dirty, effectiveCfg)
  return mintMarkdownTrustedHTML(result)
}

// The math preset's purifier is a DOMPurify instance carrying the math security
// hooks. Built lazily on first math use and memoized for the page lifetime,
// mirroring the markdown purifier.
let mathPurifier: Purifier | undefined
function getMathPurifier(): Purifier {
  mathPurifier ??= createInternalPurifier(addMathSanitizeHooks, {selfHardens: true})
  return mathPurifier
}

/**
 * Sanitize through the `math` preset: the standalone MathML config + hooks, minted
 * through the dedicated `github-sanitize-math` Trusted Types policy. Like the other
 * presets this ignores the `sanitize_dompurify_hardening` flag — the caller opted
 * into the preset explicitly.
 */
function sanitizeMath(dirty: string | Node): SanitizedHTML {
  const policy = getMathSanitizePolicy()
  // Inject the math policy so DOMPurify's internal minting stays on
  // `github-sanitize-math` rather than lazily creating its own `dompurify` policy.
  const effectiveCfg = policy
    ? {...MATH_SANITIZE_CONFIG, TRUSTED_TYPES_POLICY: policy as TrustedTypePolicy}
    : MATH_SANITIZE_CONFIG
  const result = getMathPurifier().sanitize(dirty, effectiveCfg)
  return mintMathTrustedHTML(result)
}

// The inline preset's purifier: a DOMPurify instance carrying the shared link/image
// hardening plus the inline class allowlist. Built lazily on first inline use and
// memoized for the page lifetime, mirroring the markdown purifier.
let inlinePurifier: Purifier | undefined
function getInlinePurifier(): Purifier {
  inlinePurifier ??= createInternalPurifier(addInlineSanitizeHooks, {selfHardens: true})
  return inlinePurifier
}

/**
 * Sanitize through the `inline` preset: the memex TEXT config + hooks, minted through
 * the dedicated `github-sanitize-inline` Trusted Types policy. Like `markdown`, this
 * ignores the `sanitize_dompurify_hardening` flag — the caller opted in via the preset.
 */
function sanitizeInline(dirty: string | Node): SanitizedHTML {
  const policy = getInlineSanitizePolicy()
  // Inject the inline policy so DOMPurify's internal minting stays on
  // `github-sanitize-inline` rather than lazily creating its own `dompurify` policy.
  const effectiveCfg = policy
    ? {...INLINE_SANITIZE_CONFIG, TRUSTED_TYPES_POLICY: policy as TrustedTypePolicy}
    : INLINE_SANITIZE_CONFIG
  const result = getInlinePurifier().sanitize(dirty, effectiveCfg)
  return mintInlineTrustedHTML(result)
}

/**
 * Public factory for a lax DOMPurify instance for advanced usage (e.g., adding
 * hooks). The returned purifier's `.sanitize()` is typed to return `SafeHTMLString`
 * for standard string sanitization calls.
 *
 * Every instance is lax-hardened up front (github/react-platform#1726): the shared
 * js-* / data-* / image hooks are attached before the caller's callback runs, so a
 * caller passing this purifier + a hostile `domPurifyConfig` through
 * `sanitizeWithPurifier()` cannot defeat them. There is intentionally no opt-out on
 * the public API — hardening cannot be waived by an external caller.
 */
export function createPurifier(cb?: (purifier: Purifier) => void): Purifier {
  return createInternalPurifier(cb)
}

/**
 * Internal purifier factory. `selfHardens` is a private, preset-only escape hatch:
 * the strict presets attach the unconditional `applySecurityHooks` in their own
 * callback, so they pass `selfHardens: true` to skip the auto lax-hook attach and
 * avoid double-running the js-* / data-* / image hooks. It is deliberately NOT
 * exposed on the public `createPurifier()` so no external caller can waive lax
 * hardening.
 */
function createInternalPurifier(
  cb?: (purifier: Purifier) => void,
  {selfHardens = false}: {selfHardens?: boolean} = {},
): Purifier {
  const purifier = DOMPurify() as unknown as Purifier
  if (!selfHardens) {
    applyLaxSecurityHooks(purifier)
  }
  if (cb) {
    cb(purifier)
  }
  return purifier
}

export function sanitizeWithPurifier(
  dirty: string | Node,
  purifier: Purifier,
  cfg: DOMPurifyConfig & {RETURN_DOM: true},
): Node
export function sanitizeWithPurifier(
  dirty: string | Node,
  purifier: Purifier,
  cfg: DOMPurifyConfig & {RETURN_DOM_FRAGMENT: true},
): DocumentFragment
export function sanitizeWithPurifier(
  dirty: string | Node,
  purifier: Purifier,
  cfg: DOMPurifyConfig & {RETURN_TRUSTED_TYPE: true},
): TrustedHTML
export function sanitizeWithPurifier(
  dirty: string | Node,
  purifier: Purifier,
  cfg: DOMPurifyConfig & {RETURN_TRUSTED_TYPE: false},
): SafeHTMLString
export function sanitizeWithPurifier(dirty: string | Node, purifier: Purifier, cfg?: DOMPurifyConfig): SanitizedHTML
export function sanitizeWithPurifier(
  dirty: string | Node,
  purifier: Purifier,
  cfg?: DOMPurifyConfig,
): SanitizedHTML | Node | DocumentFragment {
  // TODO: When hardening is implemented (github/react-platform#1724, github/react-platform#1725), this flag will gate
  // the DOMPurify hooks that strip js-* classes and block data-* attributes.
  const _hardeningEnabled = isFeatureEnabled('sanitize_dompurify_hardening')
  const effectiveCfg = withSanitizePolicy(cfg)
  const result = purifier.sanitize(dirty, effectiveCfg)
  // DOM / DocumentFragment / explicit Trusted Type return modes bypass the
  // string-wrapping path — DOMPurify already handled them.
  if (cfg && (cfg.RETURN_DOM || cfg.RETURN_DOM_FRAGMENT || cfg.RETURN_TRUSTED_TYPE)) {
    return result
  }
  // Caller explicitly opted out of Trusted Types — return the sanitized string
  // without minting a policy value, regardless of the feature flag. DOMPurify
  // already produced a plain string.
  if (cfg?.RETURN_TRUSTED_TYPE === false) {
    return result
  }
  return mintTrustedHTML(result)
}

/**
 * Inject our `github-sanitize-lax` policy as DOMPurify's `TRUSTED_TYPES_POLICY`
 * whenever it's available (flag on + Trusted Types available), overriding any
 * caller-supplied policy to enforce the single-named-policy guarantee.
 *
 * DOMPurify 3.4.0 mints through a Trusted Types policy on every parse, so
 * without one it lazily creates its own (non-allowlisted) `dompurify` policy.
 * Injecting on the default path — not just `RETURN_TRUSTED_TYPE: true` — keeps
 * that internal minting on `github-sanitize-lax`. The exception is an explicit
 * `RETURN_TRUSTED_TYPE: false`: the caller opted out and wants a plain string.
 */
function withSanitizePolicy<T extends DOMPurifyConfig | undefined>(cfg: T): T {
  if (cfg?.RETURN_TRUSTED_TYPE === false) {
    return cfg
  }
  const policy = getSanitizePolicy()
  if (!policy) {
    return cfg
  }
  return {...cfg, TRUSTED_TYPES_POLICY: policy as TrustedTypePolicy}
}

// eslint-disable-next-line no-barrel-files/no-barrel-files
export type {SafeHTMLString, SanitizedHTML} from './safe-html-string'
// eslint-disable-next-line no-barrel-files/no-barrel-files
export type {Config, Purifier, UponSanitizeAttributeHookEvent} from './types'
// Re-exported for the `math-renderer-element` adoption (github/react-platform#2030):
// the closed `{preset: 'math'}` path forces a string return, but one intermediate
// pass in that pipeline needs `RETURN_DOM: true`, so it builds its own purifier from
// these and runs that pass outside the closed path.
// eslint-disable-next-line no-barrel-files/no-barrel-files
export {addMathSanitizeHooks, MATH_SANITIZE_CONFIG} from './configs/math'
